/**
 * @fileoverview Renders chat message text with optional US-7.6 markdown card segments.
 * @purpose `[[card type="markdown"]]…[[/card]]` in user/assistant text becomes a card with Print/PDF (smoke + integration contract).
 */

import React from 'react';

export type ChatMessageBodyContentProps = {
  content: string | null | undefined;
  /** When true, skip card parsing so streaming cursor attachment stays valid on the plain `<p>` path in the parent. */
  skipCardParsing?: boolean;
};

function MarkdownCardBlock({ inner }: { inner: string }): React.ReactElement {
  const trimmed = inner.trim();
  return (
    <div
      data-testid="markdown-card"
      className="mt-1 rounded border p-2 text-left"
      style={{
        borderColor: 'var(--border-default)',
        backgroundColor: 'var(--background-default)',
        color: 'var(--foreground-default)',
      }}
    >
      <div className="korvochat-markdown-card-body max-h-48 overflow-y-auto text-sm whitespace-pre-wrap">{trimmed}</div>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs font-medium"
          style={{ borderColor: 'var(--border-default)', color: 'var(--foreground-default)' }}
          onClick={() => {
            window.print();
          }}
        >
          Print / PDF
        </button>
      </div>
    </div>
  );
}

function parseBody(content: string): React.ReactNode {
  const re = /\[\[card type="markdown"\]\]([\s\S]*?)\[\[\/card\]\]/gi;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (;;) {
    const m = re.exec(content);
    if (m === null) {
      break;
    }
    if (m.index > last) {
      parts.push(<span key={`s-${k++}`}>{content.slice(last, m.index)}</span>);
    }
    const cardInner = m[1];
    if (cardInner === undefined) {
      throw new Error('ChatMessageBodyContent: markdown card inner capture missing');
    }
    parts.push(<MarkdownCardBlock key={`c-${k++}`} inner={cardInner} />);
    last = m.index + m[0].length;
  }
  if (last < content.length) {
    parts.push(<span key={`s-${k++}`}>{content.slice(last)}</span>);
  }
  if (parts.length === 0) {
    return content;
  }
  return <>{parts}</>;
}

export function ChatMessageBodyContent({
  content,
  skipCardParsing = false,
}: ChatMessageBodyContentProps): React.ReactNode {
  if (content === null || content === undefined) {
    return null;
  }
  if (skipCardParsing) {
    return content;
  }
  return parseBody(content);
}
